class ProductSwitcher {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.sectionKey = section.dataset.sectionKey || 'main-product-switcher';
    this.loading = false;

    this.bindEvents();
  }

  bindEvents() {
    if (this.section.dataset.switcherBound === 'true') return;
    this.section.dataset.switcherBound = 'true';

    this.section.addEventListener('click', (event) => {
      const button = event.target.closest('.product-switcher__button[data-product-handle]');
      if (!button || !this.section.contains(button)) return;
      this.switchProduct(
        button.dataset.productHandle,
        button.dataset.switcherOption,
        button.dataset.switcherView
      );
    });

    let mobileZoomPointerStart = null;
    let lastMobileZoomTap = null;

    this.section.addEventListener('pointerdown', (event) => {
      if (
        this.section.dataset.mobileImageZoom !== 'double_tap' ||
        !window.matchMedia('(max-width: 1023px)').matches
      ) return;

      const media = event.target.closest('.product__media[data-media-type="image"]');
      if (!media || !this.section.contains(media)) return;

      mobileZoomPointerStart = {
        media,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
    });

    this.section.addEventListener('pointerup', (event) => {
      if (
        !mobileZoomPointerStart ||
        mobileZoomPointerStart.pointerId !== event.pointerId ||
        this.section.dataset.mobileImageZoom !== 'double_tap' ||
        !window.matchMedia('(max-width: 1023px)').matches
      ) return;

      const tap = mobileZoomPointerStart;
      mobileZoomPointerStart = null;

      const moved = Math.hypot(event.clientX - tap.x, event.clientY - tap.y);
      if (moved > 12) {
        lastMobileZoomTap = null;
        return;
      }

      const now = Date.now();
      const isDoubleTap =
        lastMobileZoomTap &&
        lastMobileZoomTap.media === tap.media &&
        now - lastMobileZoomTap.time <= 350;

      if (!isDoubleTap) {
        lastMobileZoomTap = { media: tap.media, time: now };
        return;
      }

      lastMobileZoomTap = null;
      const zoomTrigger = tap.media.querySelector('[data-mobile-zoom-trigger]');
      if (!zoomTrigger) return;

      event.preventDefault();
      zoomTrigger.click();
    });

    this.section.addEventListener('pointercancel', () => {
      mobileZoomPointerStart = null;
    });
  }

  setLoading(isLoading) {
    this.loading = isLoading;
    this.section.classList.toggle('is-loading', isLoading);
    this.section.querySelectorAll('.product-switcher__button[data-product-handle]').forEach((button) => {
      button.disabled = isLoading;
    });
  }

  getProductUrl(handle) {
    const rootUrl = window.theme?.routes?.root_url || '/';
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    const url = new URL(`${normalizedRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    const view = this.section.dataset.templateSuffix || currentParams.get('view');

    if (view) {
      url.searchParams.set('view', view);
    }

    ['preview_theme_id', 'preview', 'pb', '_ab', '_fd'].forEach((key) => {
      const value = currentParams.get(key);
      if (value) url.searchParams.set(key, value);
    });

    return `${url.pathname}${url.search}`;
  }

  withSearchParams(urlString, params) {
    const url = new URL(urlString, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
    return `${url.pathname}${url.search}`;
  }

  async fetchSectionHtml(handle) {
    const productUrl = this.getProductUrl(handle);
    const wrapper = this.section.closest('[id^="shopify-section-"]');
    const wrapperId = wrapper?.id?.replace('shopify-section-', '') || '';
    const instanceIds = [this.sectionId, wrapperId].filter(
      (id, index, list) => id && list.indexOf(id) === index
    );

    for (const instanceId of instanceIds) {
      const response = await fetch(
        this.withSearchParams(productUrl, { section_id: instanceId })
      );
      if (!response.ok) continue;
      const html = await response.text();
      if (html && html.includes('MainProduct-')) return html;
    }

    for (const instanceId of instanceIds) {
      const response = await fetch(
        this.withSearchParams(productUrl, { sections: instanceId })
      );
      if (!response.ok) continue;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const sections = await response.json();
        const html = sections[instanceId];
        if (html && html.includes('MainProduct-')) return html;
      } else {
        const html = await response.text();
        if (html && html.includes('MainProduct-')) return html;
      }
    }

    throw new Error('Product section request failed');
  }

  resolveActiveView(switcher, activeView, activeHandle) {
    if (!switcher) return activeView;

    const buttons = Array.from(
      switcher.querySelectorAll('.product-switcher__button[data-product-handle]')
    );
    if (!buttons.length) return activeView;

    if (activeView && buttons.some((button) => button.dataset.switcherView === activeView)) {
      return activeView;
    }

    if (activeView === 'neo') {
      const option3 = buttons.find((button) => button.dataset.switcherView === 'option3');
      if (option3 && (!activeHandle || option3.dataset.productHandle === activeHandle)) {
        return 'option3';
      }
      const option2 = buttons.find((button) => button.dataset.switcherView === 'option2');
      if (option2 && (!activeHandle || option2.dataset.productHandle === activeHandle)) {
        return 'option2';
      }
      const legacyNeo = buttons.find((button) => button.dataset.switcherView === 'neo');
      if (legacyNeo) return 'neo';
    }

    if (activeHandle) {
      const matches = buttons.filter((button) => button.dataset.productHandle === activeHandle);
      if (matches.length === 1) return matches[0].dataset.switcherView;
      if (matches.length > 1) return matches[matches.length - 1].dataset.switcherView;
    }

    return activeView;
  }

  updateSwitcherActiveState(switcher, activeHandle, activeView) {
    if (!switcher) return;

    const buttons = switcher.querySelectorAll('.product-switcher__button[data-product-handle]');
    if (!buttons.length) return;

    const resolvedView = this.resolveActiveView(switcher, activeView, activeHandle);

    buttons.forEach((button) => {
      let isActive = false;
      if (resolvedView) {
        isActive = button.dataset.switcherView === resolvedView;
      } else if (activeHandle) {
        isActive = button.dataset.productHandle === activeHandle;
      }
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  syncSwitcherActiveFromSection() {
    const switcher = this.section.querySelector('[data-switcher-preserve]');
    if (!switcher) return;
    const activeView = this.section.dataset.switcherView;
    const activeHandle = this.section.dataset.productHandle;
    this.updateSwitcherActiveState(switcher, activeHandle, activeView);
  }

  syncSectionStyles(sectionRoot, currentSectionId) {
    const currentWrapper = this.section.closest('[id^="shopify-section-"]');
    const fetchedStyle = sectionRoot?.querySelector('style');
    const currentStyle = currentWrapper?.querySelector('style');

    if (!currentStyle || !fetchedStyle) return;

    let styleText = fetchedStyle.textContent;
    const fetchedWrapperId = sectionRoot.id?.replace('shopify-section-', '');

    if (fetchedWrapperId && currentSectionId && fetchedWrapperId !== currentSectionId) {
      styleText = styleText.replaceAll(fetchedWrapperId, currentSectionId);
    }

    currentStyle.textContent = styleText;
  }

  swapProductContent(newSection, activeHandle, preferredSwitcherView) {
    const currentSection = this.section;
    const newProductInfo = newSection.querySelector('product-info');

    if (newProductInfo && !newProductInfo.dataset.productId) {
      throw new Error('Fetched product is invalid');
    }

    const currentGallery = currentSection.querySelector('.product__gallery-container');
    const newGallery = newSection.querySelector('.product__gallery-container');
    if (currentGallery && newGallery) {
      currentGallery.replaceWith(newGallery);
    }

    const currentProductInfo = currentSection.querySelector('product-info');

    if (currentProductInfo && newProductInfo) {
      currentProductInfo.abortController?.abort();
      prepareSwappedProductInfo(newProductInfo);
      if (newGallery) prepareSwappedProductInfo(newGallery);

      const preservedSwitcher = currentProductInfo.querySelector('[data-switcher-preserve]');
      const incomingSwitcher = newProductInfo.querySelector('[data-switcher-preserve]');
      const incomingNext = incomingSwitcher?.nextElementSibling || null;

      currentProductInfo.replaceWith(newProductInfo);

      const updatedProductInfo = currentSection.querySelector('product-info');
      updatedProductInfo?.querySelector('[data-switcher-preserve]')?.remove();

      if (preservedSwitcher && updatedProductInfo) {
        const insertBefore =
          incomingNext && updatedProductInfo.contains(incomingNext)
            ? incomingNext
            : updatedProductInfo.querySelector('.product-form-wrapper, .product-form') ||
              updatedProductInfo.querySelector('.product-link-item')?.nextElementSibling ||
              null;
        updatedProductInfo.insertBefore(preservedSwitcher, insertBefore);
      }

      syncProductInfoViewUrl(updatedProductInfo);

      const activeView = preferredSwitcherView || newSection.dataset.switcherView;
      if (activeView) {
        currentSection.dataset.switcherView = activeView;
      }

      this.updateSwitcherActiveState(
        preservedSwitcher || currentSection.querySelector('[data-switcher-preserve]'),
        activeHandle,
        activeView
      );
    }

    const currentSticky = currentSection.querySelector('product-sticky-form');
    const newSticky = newSection.querySelector('product-sticky-form');
    if (currentSticky && newSticky) {
      currentSticky.replaceWith(newSticky);
    } else if (currentSticky) {
      currentSticky.remove();
    } else if (newSticky) {
      currentSection.querySelector('.main-product__inner')?.appendChild(newSticky);
    }

    currentSection.querySelectorAll('script[id^="ProductJSON-"]').forEach((script) => script.remove());
    newSection.querySelectorAll('script[id^="ProductJSON-"]').forEach((script) => {
      currentSection.appendChild(script.cloneNode(true));
    });

    currentSection.dataset.productHandle = activeHandle;
  }

  syncSwitcherSplitSections(option, switcherView) {
    let view = switcherView || '';
    if (view === 'option3') {
      view = 'neo';
    } else if (view === 'option2') {
      view = 'pro';
    } else if (!view) {
      view = (option || '').toLowerCase().trim().includes('neo') ? 'neo' : 'pro';
    }
    applyCustomSectionSwitcherSplit(view);
    applyCompareTableSwitcherHighlight(view);
  }

  async switchProduct(handle, option, switcherView) {
    if (this.loading || !handle) {
      return;
    }

    const currentHandle = this.section.dataset.productHandle;
    const currentView = this.section.dataset.switcherView;
    const switcher = this.section.querySelector('[data-switcher-preserve]');

    if (handle === currentHandle) {
      if (switcherView && switcherView !== currentView) {
        this.section.dataset.switcherView = switcherView;
        this.updateSwitcherActiveState(switcher, handle, switcherView);
        this.syncSwitcherSplitSections(option, switcherView);
      }
      return;
    }

    if (switcherView) {
      this.section.dataset.switcherView = switcherView;
      this.updateSwitcherActiveState(switcher, handle, switcherView);
    }

    this.setLoading(true);

    try {
      let html = await this.fetchSectionHtml(handle);
      const currentWrapper = this.section.closest('[id^="shopify-section-"]');
      const currentSectionId = currentWrapper?.id?.replace('shopify-section-', '');

      html = normalizeSectionHtmlIds(html, currentSectionId, this.sectionId);

      const documentHTML = new DOMParser().parseFromString(html, 'text/html');
      const sectionRoot =
        documentHTML.querySelector('[id^="shopify-section-"]') || documentHTML.body;
      const newSection =
        sectionRoot.querySelector(`#MainProduct-${this.sectionId}`) ||
        sectionRoot.querySelector('[id^="MainProduct-"]') ||
        sectionRoot.querySelector('.main-product[data-section-id]');

      if (!newSection) {
        throw new Error('Product section not found');
      }

      this.syncSectionStyles(sectionRoot, currentSectionId);
      this.section.dataset.switcherSwapping = 'true';
      this.swapProductContent(newSection, handle, switcherView);
      this.syncSwitcherSplitSections(option, switcherView || newSection.dataset.switcherView);

      history.pushState({ productHandle: handle }, '', this.getProductUrl(handle));

      initializeProductSection(this.section, { afterSwap: true });
      this.section.dataset.switcherSwapping = '';
      this.setLoading(false);
    } catch (error) {
      console.error('[ProductSwitcher]', error);
      this.section.dataset.switcherSwapping = '';
      this.setLoading(false);
    }
  }
}

function getCustomSectionSwitcherItems(container) {
  const marked = container.querySelectorAll('[data-cs-switcher-item]');
  if (marked.length) {
    return Array.from(marked);
  }

  return Array.from(container.children).filter((child) => {
    if (!child || child.nodeType !== 1) return false;
    if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') return false;
    if (child.classList.contains('group-block')) return true;
    if (child.classList.contains('shopify-block')) return true;
    if (child.id && child.id.indexOf('shopify-block-') === 0) return true;
    return false;
  });
}

function resolveSwitcherView(option, fallback = 'pro') {
  const value = (option || '').toLowerCase().trim();
  if (!value) return fallback;
  if (value === 'neo') return 'neo';
  if (value === 'pro') return 'pro';
  if (value === 'option3') return 'option3';
  if (value.includes('neo')) return 'option3';
  return fallback;
}

function getActiveSwitcherView(fallback = 'pro') {
  const activeButton = document.querySelector('.product-switcher__button.is-active');
  if (activeButton?.dataset.switcherView) return activeButton.dataset.switcherView;

  const main = document.querySelector('.main-product[data-switcher-view]');
  if (main?.dataset.switcherView) return main.dataset.switcherView;

  return resolveSwitcherView(activeButton?.dataset.switcherOption, fallback);
}

function applyCompareTableSwitcherHighlight(view) {
  document.querySelectorAll('[data-compare-switcher-highlight]').forEach((el) => {
    if (el.getAttribute('data-highlight-mode') === 'lite') {
      el.setAttribute('data-switcher-view', 'lite');
      return;
    }

    const nextView = view || getActiveSwitcherView(el.getAttribute('data-switcher-view') || 'pro');
    el.setAttribute('data-switcher-view', nextView);
  });
}

function applyCustomSectionSwitcherSplit(view) {
  document.querySelectorAll('[data-cs-switcher-split]').forEach((el) => {
    const nextView = view || getActiveSwitcherView(el.getAttribute('data-switcher-view') || 'pro');
    el.setAttribute('data-switcher-view', nextView);

    const proCount = parseInt(el.getAttribute('data-pro-block-count'), 10) || 0;
    const neoCount = parseInt(el.getAttribute('data-neo-block-count'), 10) || 0;
    const items = getCustomSectionSwitcherItems(el);

    items.forEach((item, index) => {
      const set = item.getAttribute('data-cs-switcher-set');
      let show;

      if (set) {
        show = set === nextView;
      } else if (neoCount > 0) {
        if (index < proCount) {
          show = nextView === 'pro';
        } else if (index < proCount + neoCount) {
          show = nextView === 'neo' || nextView === 'option2';
        } else {
          show = nextView === 'option3';
        }
      } else {
        const isPro = index < proCount;
        if (nextView === 'pro' || nextView === 'option2') show = isPro;
        else if (nextView === 'neo') show = !isPro;
        else show = false;
      }

      item.toggleAttribute('hidden', !show);
      item.style.setProperty('display', show ? '' : 'none', 'important');
    });
  });
}

function replaceSectionIdToken(html, fromId, toId) {
  if (!html || !fromId || !toId || fromId === toId) return html;

  if (fromId === 'main-product' || fromId === 'main-product-switcher') {
    return html
      .replaceAll(`shopify-section-${fromId}`, `shopify-section-${toId}`)
      .replaceAll(`MainProduct-${fromId}`, `MainProduct-${toId}`)
      .replaceAll(`#shopify-section-${fromId}`, `#shopify-section-${toId}`);
  }

  return html.replaceAll(fromId, toId);
}

function normalizeSectionHtmlIds(html, wrapperSectionId, mainProductSectionId) {
  if (!html) return html;

  let normalized = html;

  const fetchedWrapperMatch = html.match(/id="shopify-section-([^"]+)"/);
  const fetchedMainMatch = html.match(/id="MainProduct-([^"]+)"/);

  if (fetchedWrapperMatch && wrapperSectionId && fetchedWrapperMatch[1] !== wrapperSectionId) {
    normalized = replaceSectionIdToken(normalized, fetchedWrapperMatch[1], wrapperSectionId);
  }

  if (fetchedMainMatch && mainProductSectionId && fetchedMainMatch[1] !== mainProductSectionId) {
    normalized = replaceSectionIdToken(normalized, fetchedMainMatch[1], mainProductSectionId);
  }

  return normalized.replace(/\sdata-animate="fade-up"/g, ' data-animate="none"');
}

function prepareSwappedProductInfo(el) {
  if (!el) return;
  el.setAttribute('data-animate', 'none');
  el.setAttribute('data-immediate', '');
  el.querySelectorAll('[data-animate]').forEach((node) => {
    node.setAttribute('data-animate', 'none');
  });
  el.querySelectorAll('.js-invisible').forEach((node) => {
    node.classList.remove('js-invisible');
  });
}

function syncProductInfoViewUrl(productInfo) {
  if (!productInfo) return;
  const view = productInfo.closest('.main-product')?.dataset.templateSuffix;
  const currentUrl = productInfo.getAttribute('data-product-url');
  if (!view || !currentUrl) return;

  try {
    const url = new URL(currentUrl, window.location.origin);
    url.searchParams.set('view', view);
    productInfo.setAttribute('data-product-url', `${url.pathname}${url.search}`);
  } catch (error) {
    // Keep the server-rendered product URL if it cannot be parsed.
  }
}

function forceRevealProductContent(section) {
  if (!section) return;

  const revealTargets = section.querySelectorAll(
    'product-info, media-gallery, [data-animate], animate-element, split-words, .product__title, .product__price, .product-form'
  );

  revealTargets.forEach((el) => {
    if (el.matches('.product__price, [id^="Price-"]')) return;
    if (el.matches('product-info, media-gallery, [data-animate]')) {
      el.setAttribute('data-animate', 'none');
    }
    el.style.opacity = '1';
    el.style.visibility = 'visible';
    el.style.transform = 'none';
    el.classList.add('animate');
    el.classList.remove('js-invisible');
  });

  section.querySelectorAll('media-gallery').forEach((gallery) => {
    if (typeof gallery.countMediaGallery === 'function') {
      gallery.countMediaGallery();
    }

    const slider = gallery.sliderGallery;
    if (slider && typeof slider.update === 'function') {
      slider.update();
    }
  });
}

function scheduleForceReveal(section) {
  forceRevealProductContent(section);
  requestAnimationFrame(() => forceRevealProductContent(section));
  [50, 150, 350].forEach((delay) => {
    window.setTimeout(() => forceRevealProductContent(section), delay);
  });
}

function initializeProductSection(section, { afterSwap = false } = {}) {
  if (!section) return;

  if (!section.__productSwitcher) {
    section.__productSwitcher = new ProductSwitcher(section);
  }

  section.__productSwitcher.syncSwitcherActiveFromSection();

  if (!section.dataset.switcherRevealBound) {
    section.dataset.switcherRevealBound = 'true';
    section.addEventListener('product-info:loaded', () => {
      if (section.dataset.switcherSwapping === 'true') {
        forceRevealProductContent(section);
        return;
      }
      scheduleForceReveal(section);
    });
  }

  if (afterSwap) {
    forceRevealProductContent(section);
  } else {
    scheduleForceReveal(section);
  }

  const titleElement = section.querySelector('.product__title h1, h1.heading');
  if (titleElement) {
    const shopName = (window.theme && window.theme.settings && window.theme.settings.shopName) || '';
    const productTitle = titleElement.textContent.trim();
    document.title = shopName ? `${productTitle} – ${shopName}` : productTitle;
  }

  section.dispatchEvent(new CustomEvent('product-switcher:loaded', { bubbles: true }));
}

function initAllProductSwitcherSections(scope) {
  (scope || document).querySelectorAll('[id^="MainProduct-"]').forEach((section) => {
    initializeProductSection(section);
  });
}

if (!window.__productSwitcherPopstateBound) {
  window.__productSwitcherPopstateBound = true;
  window.addEventListener('popstate', () => {
    window.location.reload();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initAllProductSwitcherSections();
    applyCustomSectionSwitcherSplit();
    applyCompareTableSwitcherHighlight();
  });
} else {
  initAllProductSwitcherSections();
  applyCustomSectionSwitcherSplit();
  applyCompareTableSwitcherHighlight();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('[id^="MainProduct-"]');
  if (section) {
    initializeProductSection(section);
  }
  applyCustomSectionSwitcherSplit();
  applyCompareTableSwitcherHighlight();
});
